import { Grid } from '@mui/material';
import React, { useState } from 'react';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { saveAutomationTemplate } from 'wisestep-api';
import { WsTypography, WsTypoVariants } from 'wisestep-base';

import { failed, success } from '../../../..//utils/NotificationMessages';
import { CampaignHeader } from '../../../../features/automatedEmailCampaignSettings';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { AddAutomationContent } from '../../features/AddAutomationCards/AddAutomationContent';

interface AddAutomationProps {
  header: React.ReactNode;
}

interface Rule {
  id: string;
  ruleType: string;
  ruleName: string;
  params: Record<string, any>;
}

interface Action {
  id: string;
  actionType: string;
  actionName: string;
  params: Record<string, any>;
}
interface AutomationFormValues {
  automationName: string;
  entityType: string;
  selectTrigger: string;
  addAutomation: boolean;
  rules: Rule[];
  actions: Action[];
}

export const AddAutomation = ({ header }: AddAutomationProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const methods = useForm<AutomationFormValues>({
    mode: 'onChange',
    defaultValues: {
      automationName: '',
      entityType: '',
      selectTrigger: '',
      addAutomation: true,
      rules: [],
      actions: [],
    },
  });

  const handleSubmit: SubmitHandler<AutomationFormValues> = async (data: any) => {
    setIsLoading(true);
    setError(null);

    try {
      const formattedRules =
        data.rules?.map((rule: any) => ({
          id: rule.id,
          ruleType: rule.ruleType,
          ruleName: rule.ruleName,
          params: rule.params,
        })) || [];

      const formattedActions =
        data.actions?.map((action: any) => {
          let params = { ...action.params };
          if (action.actionType === 'SendEmailToTeam') {
            const teamIds = (data.selectTeam || []).map((team: any) => team.id);
            params = {
              ...params,
              selectTeam: teamIds,
            };
          }

          return {
            id: action.id,
            actionType: action.actionType,
            actionName: action.actionName,
            params: params,
          };
        }) || [];

      const payloadToSend = {
        automationName: data.automationName,
        entityType: data.entityType,
        selectTrigger: data.selectTrigger,
        automationActive: data.addAutomation,
        rules: formattedRules,
        actions: formattedActions,
      };

      const response = await saveAutomationTemplate(payloadToSend);

      if (response.data.success) {
        setNotification(success(`Automation saved successfully!`));
        navigate('/automation/automation-settings');
      } else {
        throw new Error(response.data.message || 'Failed to save automation');
      }
    } catch (error: any) {
      const errorMessage = error.message ?? 'Failed to save automation';
      setNotification(failed(errorMessage));
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onReset();
    navigate('/automation/automation-settings');
  };

  const onReset = () => {
    methods.reset();
    setError(null);
  };

  return (
    <FormProvider {...methods}>
      <CampaignHeader name="addAutomation" skip={true}>
        {header}
      </CampaignHeader>
      {error && (
        <Grid xs={12} m={2}>
          <WsTypography variant={WsTypoVariants.BODY1}>{error}</WsTypography>
        </Grid>
      )}
      <Grid xs={12} m={2}>
        <AddAutomationContent
          isEditMode={false}
          onSubmit={methods.handleSubmit(handleSubmit)}
          onCancel={handleCancel}
          isLoading={isLoading}
          showActions={true}
        />
      </Grid>
    </FormProvider>
  );
};
